export type LocationPermissionStatus =
  | "granted"
  | "denied"
  | "blocked"
  | "unknown";

export type MapCoordinate = { latitude: number; longitude: number };

export async function checkLocationPermission(): Promise<LocationPermissionStatus> {
  if (!navigator.geolocation) return "blocked";
  if (!navigator.permissions?.query) return "unknown";

  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    if (result.state === "granted") return "granted";
    if (result.state === "denied") return "denied";
    return "unknown";
  } catch {
    return "unknown";
  }
}

export async function requestLocationPermission(): Promise<LocationPermissionStatus> {
  if (!navigator.geolocation) return "blocked";

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve("granted"),
      (error) => {
        if (error.code === error.PERMISSION_DENIED) resolve("denied");
        else resolve("unknown");
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60_000 }
    );
  });
}

export function getCurrentCoordinates(options?: {
  timeoutMs?: number;
}): Promise<MapCoordinate | null> {
  if (!navigator.geolocation) return Promise.resolve(null);

  const timeoutMs = options?.timeoutMs ?? 12_000;

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(null), timeoutMs);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.clearTimeout(timer);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        window.clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 }
    );
  });
}
